import {CommonModule} from '@angular/common';
import {HttpParams} from '@angular/common/http';
import {Component, Inject, OnDestroy, OnInit, signal} from '@angular/core';
import {ReactiveFormsModule, UntypedFormControl} from '@angular/forms';
import {MatDialogModule, MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatListModule, MatSelectionListChange} from '@angular/material/list';
import {TranslateModule} from '@ngx-translate/core';
import {Q9HtmlEncoderPipe} from '@q9elements/ui-kit/common';
import {
  ContentLoaderComponent,
  DialogCloseButtonComponent,
  InfoMessageComponent,
  PrimaryButtonComponent,
  SecondaryButtonComponent,
  TabPlaceholderComponent,
  TextFieldComponent
} from '@q9elements/ui-kit/components';
import {CustomEncoder} from 'src/app/core/helpers/custom-encoder';
import {RequestTask} from 'src/app/shared/models/request-task.interface';
import {RequestsService} from 'src/app/core/requests/requests.service';
import {
  BehaviorSubject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap
} from 'rxjs';
import {DrilldownService} from 'src/app/diagram-interaction/services/drilldown.service';
import {DiagramJsonHandlerService} from 'src/app/diagram-data/diagram-json-handler.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../shared/models/diagram-version.constant';

@Component({
  selector: 'app-use-existing-diagram-dialog',
  templateUrl: './use-existing-diagram-dialog.component.html',
  styleUrls: ['../import-drilldown-dialog/import-drilldown-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatListModule,
    TranslateModule,
    ReactiveFormsModule,
    DialogCloseButtonComponent,
    SecondaryButtonComponent,
    PrimaryButtonComponent,
    InfoMessageComponent,
    TextFieldComponent,
    ContentLoaderComponent,
    Q9HtmlEncoderPipe,
    MatIconModule,
    TabPlaceholderComponent
  ]
})
export class UseExistingDiagramDialogComponent implements OnInit, OnDestroy {
  private diagramSearchSubject = new BehaviorSubject([]);
  private stateToSearch = DIAGRAM_VERSION_STATES_ENUM.DRAFT;
  private searchSubscription: Subscription;

  public error = '';
  public diagrams$ = this.diagramSearchSubject.asObservable();
  public selectedDiagram: string | null = null;
  public firstStart = true;
  public searchControl = new UntypedFormControl('');
  public diagramVersionId = this.diagramJsonSvc.get().id;
  public isLoading = signal(false);

  constructor(
    private requestsSvc: RequestsService,
    private drilldownSvc: DrilldownService,
    @Inject(MAT_DIALOG_DATA) protected passedData: any,
    private dialogRef: MatDialogRef<UseExistingDiagramDialogComponent>,
    private diagramJsonSvc: DiagramJsonHandlerService
  ) {}

  ngOnInit(): void {
    this.setObserverOnSearch();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  private taskForSearch(text: string): RequestTask {
    const params = new HttpParams({encoder: new CustomEncoder()})
      .set('query', text)
      .set('type', 'all')
      .set('state', this.stateToSearch);

    return {
      isApi: true,
      method: 'GET',
      url: `/search/map`,
      queryParams: params
    };
  }

  private setObserverOnSearch(): void {
    this.searchSubscription = this.searchControl.valueChanges
      .pipe(
        debounceTime(400),
        filter(value => !!value),
        distinctUntilChanged()
      )
      .pipe(
        switchMap(text => {
          this.selectedDiagram = null;
          this.isLoading.set(true);

          return this.requestsSvc.addToQueue(this.taskForSearch(text), true);
        })
      )
      .subscribe((result: {count: number; diagrams: []}) => {
        this.isLoading.set(false);
        this.firstStart = false;

        if (result) {
          this.diagramSearchSubject.next(result.diagrams);
        }
      });
  }

  public changeSelected(event: MatSelectionListChange): void {
    const [option] = event.options;

    this.selectedDiagram = option.value.diagramId;
    this.error = '';
  }

  public useExistingDiagram(): void {
    if (!this.selectedDiagram) {
      this.error = 'POPUPS.NO_SELECTED_DIAGRAM';

      return;
    }

    if (this.selectedDiagram === this.diagramVersionId) {
      return;
    }

    this.dialogRef.close();

    return this.drilldownSvc.useExistingDiagram(this.passedData.cell, this.selectedDiagram);
  }
}
